
export function cleanGutenbergText(text: string, includeFrontMatter: boolean = false): string {
    if (includeFrontMatter) return text;

    // Common markers
    const startMarkers = [
        /\*\*\* ?START OF (THE|THIS) PROJECT GUTENBERG EBOOK .+ \*\*\*/i,
        /\*\*\* ?START OF THE PROJECT GUTENBERG EBOOK/i,
        /^\*\*\* ?START OF THIS PROJECT GUTENBERG EBOOK/i
    ];

    const endMarkers = [
        /\*\*\* ?END OF (THE|THIS) PROJECT GUTENBERG EBOOK .+ \*\*\*/i,
        /\*\*\* ?END OF THE PROJECT GUTENBERG EBOOK/i,
        /^\*\*\* ?END OF THIS PROJECT GUTENBERG EBOOK/i
    ];

    let startIndex = 0;
    let endIndex = text.length;

    for (const marker of startMarkers) {
        const match = text.match(marker);
        if (match && match.index !== undefined) {
            // Start AFTER the marker + newline
            startIndex = match.index + match[0].length;
            break;
        }
    }

    for (const marker of endMarkers) {
        const match = text.match(marker);
        if (match && match.index !== undefined) {
            endIndex = match.index;
            break;
        }
    }

    let body = text.slice(startIndex, endIndex).trim();

    // Heuristic: If we trimmed too much or found no markers, maybe return original?
    // But Gutenberg pretty much always has them.
    // If the body is overly short (e.g. empty), fallback to original to avoid blank screen.
    if (body.length < 100 && text.length > 1000) {
        console.warn("Project Gutenberg cleaner resulted in empty text, reverting to original.");
        return text;
    }

    return body;
}

export function splitGutenbergChapters(text: string): string[] {
    // Normalise newlines to \n
    const normalized = text.replace(/\r\n/g, '\n');
    
    // Split by lines to analyze and detect chapter start lines
    const lines = normalized.split('\n');
    
    // Patterns that identify standard chapter headings at the start of a line
    const chapterPattern = /^\s*(?:CHAPTER|Chapter|Letter|LETTER|Book|BOOK|Section|SECTION|Part|PART|VOLUME|Volume)\s+(?:[IVXLCDM\d]+|[a-zA-Z\d\-]+)/;
    
    const chapters: string[] = [];
    let currentChapterLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // If line is a chapter heading and we already accumulated text, start a new chapter
        if (chapterPattern.test(line)) {
            // Trim and push the previous chapter if it has text
            const currentText = currentChapterLines.join('\n').trim();
            if (currentText.length > 0) {
                chapters.push(currentText);
                currentChapterLines = [];
            }
        }
        currentChapterLines.push(line);
    }
    
    // Push the final chapter
    const lastText = currentChapterLines.join('\n').trim();
    if (lastText.length > 0) {
        chapters.push(lastText);
    }
    
    // Fallback: If no chapter markers were found or we got a single huge block, split by paragraph chunks to avoid a single page
    if (chapters.length <= 1) {
        const paragraphs = normalized.split(/\n\s*\n+/);
        const chunked: string[] = [];
        let currentChunk: string[] = [];
        
        for (const p of paragraphs) {
            const trimmed = p.trim();
            if (!trimmed) continue;
            currentChunk.push(trimmed);
            // Group paragraphs into chunks of 45-50 paragraphs each
            if (currentChunk.length >= 45) {
                chunked.push(currentChunk.join('\n\n'));
                currentChunk = [];
            }
        }
        if (currentChunk.length > 0) {
            chunked.push(currentChunk.join('\n\n'));
        }
        
        // Return chunked paragraphs if we split them successfully, otherwise return original text as single item
        return chunked.length > 0 ? chunked : [text];
    }
    
    return chapters;
}
